import { useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  MapPin,
  Building2,
  Briefcase,
  Clock,
  DollarSign,
  Users,
  Share2,
  Bookmark,
  CheckCircle,
} from 'lucide-react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { CareerPageLayout } from '@/components/features/career/CareerPageLayout'
import { ApplicationForm } from '@/components/features/career/ApplicationForm'

interface Job {
  id: string
  title: string
  description: string
  requirements?: string
  benefits?: string
  department?: { name: string }
  location?: { name: string }
  employmentType: string
  experienceLevel?: string
  remotePolicy: string
  salaryMin?: number
  salaryMax?: number
  salaryCurrency?: string
  salaryPeriod?: string
  showSalary?: boolean
  createdAt: string
  customQuestions?: {
    id: string
    type: 'text' | 'textarea' | 'select' | 'radio' | 'checkbox'
    question: string
    required: boolean
    options?: { label: string; value: string }[]
  }[]
}

interface PublicJobResponse {
  company: {
    name: string
    logo: string | null
  }
  job: Job
}

export function PublicJobDetailPage() {
  const { tenant: tenantSlug, slug: jobSlug } = useParams({ strict: false }) as { tenant: string; slug: string }
  const [showApplication, setShowApplication] = useState(false)
  const [applicationSubmitted, setApplicationSubmitted] = useState(false)

  // Fetch job details (includes company info)
  const { data: jobResponse, isLoading } = useQuery({
    queryKey: ['public-job', tenantSlug, jobSlug],
    queryFn: () => api.get<PublicJobResponse>(`/public/${tenantSlug}/jobs/${jobSlug}`),
  })

  const companyInfo = (jobResponse as PublicJobResponse)?.company
  const pageSettings = {
    companyName: companyInfo?.name || tenantSlug,
    companyLogo: companyInfo?.logo || undefined,
  }

  const jobData = (jobResponse as PublicJobResponse)?.job

  const formatSalary = (min?: number, max?: number, currency?: string, period?: string) => {
    if (!min && !max) return null
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0,
    })
    const periodLabel = period === 'yearly' ? '/year' : period === 'monthly' ? '/month' : '/hour'

    if (min && max) {
      return `${formatter.format(min)} - ${formatter.format(max)}${periodLabel}`
    }
    return `${formatter.format(min || max!)}${periodLabel}`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${jobData.title} at ${pageSettings.companyName}`,
          url: window.location.href,
        })
      } catch (err) {
        // User cancelled or error
      }
    } else {
      navigator.clipboard.writeText(window.location.href)
    }
  }

  if (isLoading) {
    return (
      <CareerPageLayout settings={pageSettings} tenantSlug={tenantSlug}>
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="h-8 w-48 skeleton" />
          <div className="h-64 skeleton rounded-xl" />
          <div className="h-96 skeleton rounded-xl" />
        </div>
      </CareerPageLayout>
    )
  }

  if (!jobData) {
    return (
      <CareerPageLayout settings={pageSettings} tenantSlug={tenantSlug}>
        <div className="text-center py-16">
          <Briefcase className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Job not found</h2>
          <p className="text-muted-foreground mb-4">
            This position may have been filled or removed
          </p>
          <Button asChild>
            <a href={`/careers/${tenantSlug}`}>View all positions</a>
          </Button>
        </div>
      </CareerPageLayout>
    )
  }

  if (applicationSubmitted) {
    return (
      <CareerPageLayout settings={pageSettings} tenantSlug={tenantSlug}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl mx-auto text-center py-16"
        >
          <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-success" />
          </div>
          <h2 className="text-2xl font-bold mb-4">Application Submitted!</h2>
          <p className="text-muted-foreground mb-6">
            Thank you for applying for the {jobData.title} position at {pageSettings.companyName}.
            We've received your application and will review it shortly.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            You should receive a confirmation email with next steps.
          </p>
          <Button asChild>
            <a href={`/careers/${tenantSlug}`}>View more positions</a>
          </Button>
        </motion.div>
      </CareerPageLayout>
    )
  }

  return (
    <CareerPageLayout settings={pageSettings} tenantSlug={tenantSlug}>
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <a
          href={`/careers/${tenantSlug}`}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to all positions
        </a>

        {/* Job Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold mb-3">{jobData.title}</h1>
              <div className="flex flex-wrap items-center gap-3 text-muted-foreground">
                {jobData.department && (
                  <span className="flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    {jobData.department.name}
                  </span>
                )}
                {jobData.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {jobData.location.name}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Posted {formatDate(jobData.createdAt)}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handleShare}>
                <Share2 className="h-4 w-4" />
              </Button>
              <Button onClick={() => setShowApplication(true)}>
                Apply Now
              </Button>
            </div>
          </div>
        </motion.div>

        <div className="grid gap-8 md:grid-cols-3">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-8">
            {/* Description */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="text-xl font-semibold mb-4">About this role</h2>
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: jobData.description }}
              />
            </motion.section>

            {/* Requirements */}
            {jobData.requirements && (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h2 className="text-xl font-semibold mb-4">Requirements</h2>
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: jobData.requirements }}
                />
              </motion.section>
            )}

            {/* Benefits */}
            {jobData.benefits && (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="text-xl font-semibold mb-4">Benefits</h2>
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: jobData.benefits }}
                />
              </motion.section>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Job Details Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Employment Type</p>
                    <p className="font-medium capitalize">
                      {jobData.employmentType.replace('-', ' ')}
                    </p>
                  </div>

                  {jobData.experienceLevel && (
                    <div>
                      <p className="text-sm text-muted-foreground">Experience Level</p>
                      <p className="font-medium capitalize">{jobData.experienceLevel}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm text-muted-foreground">Work Style</p>
                    <p className="font-medium capitalize">{jobData.remotePolicy}</p>
                  </div>

                  {jobData.showSalary && (jobData.salaryMin || jobData.salaryMax) && (
                    <div>
                      <p className="text-sm text-muted-foreground">Salary Range</p>
                      <p className="font-medium flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        {formatSalary(
                          jobData.salaryMin,
                          jobData.salaryMax,
                          jobData.salaryCurrency,
                          jobData.salaryPeriod
                        )}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Apply CTA Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="bg-primary text-primary-foreground">
                <CardContent className="p-6 text-center">
                  <Users className="h-8 w-8 mx-auto mb-3 opacity-80" />
                  <h3 className="font-semibold mb-2">Interested?</h3>
                  <p className="text-sm opacity-90 mb-4">
                    Apply now and join our team
                  </p>
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => setShowApplication(true)}
                  >
                    Apply for this position
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>

        {/* Application Form Modal */}
        {showApplication && (
          <ApplicationForm
            open={showApplication}
            onOpenChange={setShowApplication}
            jobId={jobData.id}
            jobTitle={jobData.title}
            companyName={pageSettings.companyName}
            tenantSlug={tenantSlug}
            customQuestions={jobData.customQuestions}
            onSuccess={() => setApplicationSubmitted(true)}
          />
        )}
      </div>
    </CareerPageLayout>
  )
}
